// features/premium/FreeView.tsx — not-subscribed state: hero + billing toggle + 3 plan cards.
import { useState } from 'react';
import styled from 'styled-components';
import { usePremium } from '@/hooks';
import type { PremiumTierId } from '@/core/models/premium';
import { media } from '@/theme/media';
import { BillingToggle } from './BillingToggle';
import type { BillingPeriod } from './BillingToggle';
import { PlanCard } from './PlanCard';

export interface FreeViewProps {
  onUpgrade: (id: PremiumTierId) => void;
  /** Fix-round F1: Checkout is unconfigured server-side — see PlanCard. */
  billingUnavailable?: boolean;
}

const Hero = styled.div`
  padding: 24px; margin-bottom: 18px; border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
`;
const HeroTitle = styled.h2`
  margin: 0 0 6px; font-family: ${({ theme }) => theme.font.display};
  font-size: ${({ theme }) => theme.fontSize.xl}; color: ${({ theme }) => theme.color.textPrimary};
`;
const HeroSub = styled.p`margin: 0; font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.color.textMuted};`;
const ToolbarRow = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 14px; flex-wrap: wrap;`;
const ChooseLabel = styled.h3`margin: 0; font-family: ${({ theme }) => theme.font.display}; font-size: ${({ theme }) => theme.fontSize.md}; color: ${({ theme }) => theme.color.textPrimary};`;
const Plans = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
  ${media.mobile`grid-template-columns: 1fr;`}
`;

export function FreeView({ onUpgrade, billingUnavailable = false }: FreeViewProps) {
  const { tiers } = usePremium();
  // Fix-round F12: MONTHLY is the default, because monthly is the only price
  // that exists. Defaulting to annual rendered "$49.99/yr" above a live
  // Upgrade button that enrolled the payer in $4.99/mo billing.
  const [period, setPeriod] = useState<BillingPeriod>('monthly');
  return (
    <div>
      {/* Fix-round F9: this hero used to read "More power. More reach. More
          you." over "earn from what you create" — Pro sells neither reach nor
          earnings, and never will (selling feed reach is forbidden by owner
          ruling). Copy here may only name what Pro actually delivers.
          premium-absence.test.ts scans this file's rendered copy. */}
      <Hero>
        <HeroTitle>Back the platform you use.</HeroTitle>
        <HeroSub>
          Pro adds a supporter badge, profile flair, an unlimited edit window and analytics on
          your own posts, and keeps TruXSocial running without ads or data sales.
        </HeroSub>
      </Hero>
      <ToolbarRow>
        <ChooseLabel>Choose your plan</ChooseLabel>
        <BillingToggle value={period} onChange={setPeriod} />
      </ToolbarRow>
      <Plans>
        {tiers.map((t) => (
          <PlanCard
            key={t.id}
            tier={t}
            period={period}
            onUpgrade={onUpgrade}
            billingUnavailable={billingUnavailable}
          />
        ))}
      </Plans>
    </div>
  );
}
